import { Col, Icon, Picture, Tappable, Text, useTheme, type ViewStyle } from "@rally/ui-kit";
import { say } from "../../localization/say.ts";
import { farAlong, type Picked, tileSaid } from "./attaching.ts";
import { glyphFor, typeSaid } from "./filing.ts";

const TILE = 56;

export function Attached({ picked, onDrop }: { picked: Picked[]; onDrop: (key: string) => void }) {
  const theme = useTheme();
  if (picked.length === 0) return null;
  return (
    <Col
      testID="attached"
      style={{ flexDirection: "row", flexWrap: "wrap", gap: theme.space[2], paddingBottom: theme.space[1] }}
    >
      {picked.map((one) => (
        <Tile key={one.key} one={one} onDrop={() => onDrop(one.key)} />
      ))}
    </Col>
  );
}

function Tile({ one, onDrop }: { one: Picked; onDrop: () => void }) {
  const theme = useTheme();
  const failed = one.state === "failed";
  return (
    <Col
      testID={`tile-${one.key}`}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: theme.space[2],
        borderWidth: 1,
        borderColor: failed ? theme.color.danger : theme.color["border.default"],
        borderRadius: theme.radius.md,
        backgroundColor: theme.color["bg.surfaceAlt"],
        padding: theme.space[2],

        maxWidth: 260,
      }}
    >
      <Col
        style={{
          width: TILE,
          height: TILE,
          borderRadius: theme.radius.sm,
          overflow: "hidden",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: theme.color["bg.surface"],
        }}
      >
        {one.preview ? (
          <Picture
            source={{ uri: one.preview }}
            accessibilityLabel={one.name}
            style={{ width: TILE, height: TILE }}
          />
        ) : (
          <Icon name={glyphFor(one)} size={20} color="text.secondary" />
        )}
      </Col>

      <Col style={{ flex: 1, minWidth: 0, gap: 2 }}>
        <Text typeName="caption" color="text.primary" numberOfLines={1}>
          {one.name}
        </Text>
        <Text typeName="caption" color={failed ? "danger" : "text.tertiary"} numberOfLines={2}>
          {failed ? (one.why ?? say("files.broke")) : `${typeSaid(one)} · ${tileSaid(one)}`}
        </Text>
        <Col
          testID={`climbing-${one.key}`}
          style={{
            height: 3,
            borderRadius: 999,
            overflow: "hidden",
            backgroundColor: theme.color["bg.surface"],
            opacity: one.state === "climbing" ? 1 : 0,
          }}
        >
          <Col
            style={
              {
                width: `${farAlong(one) * 100}%`,
                height: 3,
                backgroundColor: theme.color.accent,
                transitionProperty: "width",
                transitionDuration: theme.preferReduced ? "0ms" : "180ms",
              } as ViewStyle
            }
          />
        </Col>
      </Col>

      <Tappable
        onPress={onDrop}
        accessibilityRole="button"
        accessibilityLabel={say("files.remove")}
        testID={`unattach-${one.key}`}
        style={{ width: 22, height: 22, alignItems: "center", justifyContent: "center" }}
      >
        <Icon name="close" size={14} color="text.tertiary" />
      </Tappable>
    </Col>
  );
}
